import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import {
  compactNavigation,
  defaultNavigation,
  futuristicNavigation,
  horizontalNavigation
} from 'app/mock-api/common/navigation/data';
import { environment } from 'environments/environment';
import { UserService } from 'app/shared/services/actors-users/user.service';
import { Observable, catchError, map, of } from 'rxjs';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root'
})
export class NavigationMockApi {

  private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
  private _defaultNavigation: FuseNavigationItem[] = [];
  private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
  private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

  /**
   * Constructor
   */
  constructor(
    private _fuseMockApiService: FuseMockApiService,
    private _userService: UserService
  ) {
    // Register Mock API handlers
    this.registerHandlers();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Génère la navigation compacte
   */
  public getAllowedDefaultNavigation(email: string): Observable<FuseNavigationItem[]> {
    return this._userService.getAllowedPermissionsByEmail(email).pipe(
      map(permissions => {
        this._defaultNavigation = defaultNavigation;
        const filteredNavigation = [...this._defaultNavigation];
        filteredNavigation.forEach(item => {
          this.filterNavigationItem(item, permissions);
        });
        return filteredNavigation;
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  // Méthode récursive pour filtrer les éléments de navigation
  private filterNavigationItem(
    item: FuseNavigationItem,
    permissions: any[],
    parentAllowed = false
  ): void {
    const matchingPermission = permissions.find(permission => permission.id === item.id);
    const isNavigationGroup = item.meta?.navigationGroup === true;

    // CPC pages inherit the parent permission because RoleGuard already grants
    // child URLs when their parent navigation route is authorized. Visual groups
    // reuse their children's existing permission scope.
    const isAllowed = isNavigationGroup
      ? parentAllowed
      : !!matchingPermission ||
        (item.meta?.inheritParentPermission === true && parentAllowed);

    // Masquer l'élément si non autorisé
    item.hidden = (): boolean => !isAllowed;

    // Si l'élément est autorisé, mettre à jour ses propriétés
    if (isAllowed && item.children && Array.isArray(item.children)) {
      const childPermissions = isNavigationGroup
        ? permissions
        : matchingPermission?.children || [];
      item.children.forEach(child => {
        this.filterNavigationItem(child, childPermissions, isAllowed);
      });
    }
  }

  /**
   * Génère le lien pour "Espace partenaire SICA"
   */
  private buildSicaLink(): string {
    const accessToken = localStorage.getItem('accessToken') || '';
    const refreshToken = encodeURIComponent(localStorage.getItem('refreshToken') || '');
    return `${environment.SICA}splashscreen?token=${accessToken}&refreshToken=${refreshToken}&flag=false`;
  }

/**
   * Génère le lien pour "Espace ACEP"
   */
private buildAcepLink(): string {
  const accessToken = localStorage.getItem('accessToken') || '';
  const refreshToken = encodeURIComponent(localStorage.getItem('refreshToken') || '');
  return `${environment.ACEP}redirect/gotopage?token=${accessToken}&refreshToken=${refreshToken}&flag=false`;
}


  /**
   * Register Mock API handlers
   */
  registerHandlers(): void {
    // -----------------------------------------------------------------------------------------------------
    // @ Navigation - GET
    // -----------------------------------------------------------------------------------------------------
    this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
      // Fill compact navigation children using the default navigation
      this._compactNavigation.forEach(compactNavItem => {
        this._defaultNavigation.forEach(defaultNavItem => {
          if (defaultNavItem.id === compactNavItem.id) {
            compactNavItem.children = cloneDeep(defaultNavItem.children);
          }
        });
      });

      // Fill futuristic navigation children using the default navigation
      this._futuristicNavigation.forEach(futuristicNavItem => {
        this._defaultNavigation.forEach(defaultNavItem => {
          if (defaultNavItem.id === futuristicNavItem.id) {
            futuristicNavItem.children = cloneDeep(defaultNavItem.children);
          }
        });
      });

      // Fill horizontal navigation children using the default navigation
      this._horizontalNavigation.forEach(horizontalNavItem => {
        this._defaultNavigation.forEach(defaultNavItem => {
          if (defaultNavItem.id === horizontalNavItem.id) {
            horizontalNavItem.children = cloneDeep(defaultNavItem.children);
          }
        });
      });

      // Met à jour les tokens pour les liens dépendants
      this._defaultNavigation.forEach(item => {
        if (item.id === 'espace_partenaire_sica') {
          item.link = this.buildSicaLink();
        }
      });

      this._defaultNavigation.forEach(item => {
        if (item.id === 'espace_acep') {
          item.link = this.buildAcepLink();
        }
      });
      // Return the response
      return [
        200,
        {
          compact: cloneDeep(this._compactNavigation),
          default: cloneDeep(this._defaultNavigation),
          futuristic: cloneDeep(this._futuristicNavigation),
          horizontal: cloneDeep(this._horizontalNavigation)
        }
      ];
    });
  }
}
